import React, { useCallback, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  PROJECT_FIELD_LIMITS,
  DOWNLOADABLE_ASSETS,
  TacoAssetType,
} from "@amzn/afxtaco-types";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Folder,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Moon,
  Sun,
} from "lucide-react";
import { cn } from "@/lib/utils";
import IDEChatPanel from "./IDEChatPanel";
import { ResizeHandle } from "./ui/resize-handle";
import { useResizablePanel } from "../hooks/useResizablePanel";
import { useTheme } from "next-themes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useProjects } from "../hooks/useProjects";
import { useAdditionalAssets } from "../hooks/useAdditionalAssets";
import { useProjectAsset } from "../hooks/useProjectAsset";
import { useAssetManifest } from "../hooks/useAssetManifest";
import { useFeatureFlags } from "../utils/FeatureFlagDetection";
import { recordEvent } from "../monitoring";
import DownloadProjectButton from "./download/DownloadProjectButton";
import Footer from "./Footer";

/** Chat panel sizing constants — shared between hook and aria attributes */
const CHAT_PANEL_DEFAULT_WIDTH = 384;
const CHAT_PANEL_MIN_WIDTH = 280;
const CHAT_PANEL_MAX_WIDTH = 700;

interface IDELayoutProps {
  children?: React.ReactNode;
}

const IDELayout = ({ children }: IDELayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);

  // Help panel state — shared with ArchitectureComposerPage via Outlet context.
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);

  const {
    width: panelWidth,
    isDragging,
    handleMouseDown,
    resizeByDelta,
  } = useResizablePanel({
    defaultWidth: CHAT_PANEL_DEFAULT_WIDTH,
    minWidth: CHAT_PANEL_MIN_WIDTH,
    maxWidth: CHAT_PANEL_MAX_WIDTH,
    isCollapsed: isLeftPanelCollapsed,
  });

  const {
    getProjectById,
    updateProject,
    deleteProject: deleteProjectAPI,
  } = useProjects();

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // FF: render the project download split button in the project header.
  // When OFF the header looks identical to today.
  const {
    isProjectDownloadEnabled,
    isBlogPostEnabled,
    isAdditionalAssetsEnabled,
    isPresentationDownloadEnabled,
  } = useFeatureFlags([
    "projectDownload",
    "blogPost",
    "additionalAssets",
    "presentationDownload",
  ]);

  const handleToggleCollapse = () => {
    setIsLeftPanelCollapsed(!isLeftPanelCollapsed);
  };

  // Shared via Outlet context so a tab can hand the user back to the chat. The
  // Overview prefills the input, which would be invisible if the panel were
  // collapsed — and would look like the button did nothing.
  const expandChatPanel = useCallback(() => {
    setIsLeftPanelCollapsed(false);
  }, []);

  // Extract project ID from URL
  const projectId = location.pathname.split("/project/")[1];

  // Get current project from Redux store
  const currentProject = projectId ? getProjectById(projectId) : null;

  // Additional (non-canonical) assets for the download dropdown/bundle. Skipped
  // unless both download and additionalAssets are enabled. Reads from the same
  // shared cache the asset tabs populate (no duplicate fetch when already loaded).
  const { assets: additionalAssets } = useAdditionalAssets({
    projectId,
    skip: !isProjectDownloadEnabled || !isAdditionalAssetsEnabled,
  });

  // Detect whether this project actually has a drawio diagram. The diagram
  // downloads (.drawio and the rendered .svg) depend on it existing — without
  // this a partially-generated project (latestVersion >= 1 but no diagram)
  // would incorrectly enable those items. Reuses the shared BIDI_DIAGRAM asset
  // cache (the Architecture tab populates it; no duplicate fetch when loaded).
  const diagramAsset = useProjectAsset({
    projectId,
    assetType: TacoAssetType.BIDI_DIAGRAM,
    skip: !isProjectDownloadEnabled,
  });
  // Gate on whether the diagram is known to NOT exist (confirmed 404). Using
  // `!isNotFound` rather than checking `url/content` presence avoids a flicker:
  // DrawioEditor's useProjectAsset clears the Redux blob URL on unmount (tab
  // switch), which would briefly make url===undefined even though the diagram
  // exists in S3. A confirmed 404 is the only real "no diagram" signal.
  const hasDiagram = !diagramAsset.isNotFound;

  // Source-of-truth for whether the project has any generated assets in S3.
  const { hasAssets: hasGeneratedAssets } = useAssetManifest({
    projectId,
    skip: !isProjectDownloadEnabled,
  });

  // Set the max name and description length limits. Since the name and description could be LLM generated, if the LLM generated
  // content exceeding the limits, use that as the maximum
  const nameMaxLength = Math.max(
    PROJECT_FIELD_LIMITS.projectName,
    currentProject?.projectName?.length ?? 0,
  );
  const descMaxLength = Math.max(
    PROJECT_FIELD_LIMITS.projectDescription,
    currentProject?.projectDescription?.length ?? 0,
  );

  const handleRenameClick = () => {
    if (currentProject) {
      setNewProjectName(currentProject.projectName ?? "");
      setNewProjectDescription(currentProject.projectDescription || "");
      setRenameDialogOpen(true);
    }
  };

  const handleRenameConfirm = async () => {
    if (!currentProject || !newProjectName.trim() || !projectId) return;

    try {
      await updateProject(projectId, {
        projectName: newProjectName.trim(),
        projectDescription: newProjectDescription.trim(),
      });

      setRenameDialogOpen(false);
      setNewProjectName("");
      setNewProjectDescription("");
    } catch (error) {
      console.error("Failed to rename project:", error);
      // You could add toast notification here
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentProject || !projectId) return;

    try {
      // Navigate away first so child components unmount before the
      // project is removed from Redux, preventing stale data fetches.
      setDeleteDialogOpen(false);
      navigate("/projects");

      await deleteProjectAPI(projectId);
      recordEvent("taco.project.deleted", { projectId });
    } catch (error) {
      console.error("Failed to delete project:", error);
      // You could add toast notification here
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Top Navigation */}
      <div className="sticky top-0 z-50 h-14 border-b border-border bg-background flex items-center px-4 gap-4">
        {/* Left: Logo and description */}
        <div
          className="flex items-center gap-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => (window.location.href = "/")}
        >
          <img
            src="/logo.png"
            alt="TACO Logo"
            className="w-5 h-5 object-contain"
          />
          <span className="font-semibold text-foreground">TACO</span>
          <div className="w-px h-4 bg-gray-300 mx-1"></div>
          <span className="text-sm text-muted-foreground">
            Technical Architecture Composer
          </span>
        </div>

        {/* Right: My Projects, Theme, User */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => navigate("/projects")}
          >
            <Folder className="h-4 w-4" />
            My Projects
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 gap-2"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Chat Panel - resizable */}
        <div
          className={cn(
            "flex-shrink-0 bg-background overflow-hidden",
            // Only animate width when collapsing/expanding, not during drag
            !isDragging && "transition-all duration-300",
            // When collapsed the ResizeHandle is hidden, so restore the border
            isLeftPanelCollapsed && "border-r border-border",
          )}
          style={{ width: isLeftPanelCollapsed ? 64 : panelWidth }}
        >
          <IDEChatPanel
            isCollapsed={isLeftPanelCollapsed}
            onToggleCollapse={handleToggleCollapse}
            onHelpClick={() => {
              setIsHelpPanelOpen(true);
              if (projectId) {
                navigator.clipboard.writeText(projectId);
              }
            }}
          />
        </div>

        {/* Resize Handle — hidden when panel is collapsed */}
        {!isLeftPanelCollapsed && (
          <ResizeHandle
            isDragging={isDragging}
            onMouseDown={handleMouseDown}
            onKeyboardResize={resizeByDelta}
            ariaValueNow={panelWidth}
            ariaValueMin={CHAT_PANEL_MIN_WIDTH}
            ariaValueMax={CHAT_PANEL_MAX_WIDTH}
          />
        )}

        {/* Right Side - Project Header + Content */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Project Header */}
          {currentProject && (
            <div className="p-4 border-b border-border bg-background">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-lg font-semibold text-foreground">
                      {currentProject.projectName}
                    </h1>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {currentProject.projectDescription}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {isProjectDownloadEnabled &&
                    projectId &&
                    (() => {
                      // Diagram downloads (.drawio + .svg) require the diagram
                      // to actually exist in S3.
                      const diagramDependentTypes = new Set<TacoAssetType>([
                        TacoAssetType.BIDI_DIAGRAM,
                        TacoAssetType.ARCHITECTURE_DIAGRAM_SVG,
                      ]);
                      const availableAssetTypes = hasGeneratedAssets
                        ? new Set(
                            DOWNLOADABLE_ASSETS.map((m) => m.type).filter(
                              (t) =>
                                !diagramDependentTypes.has(t) || hasDiagram,
                            ),
                          )
                        : new Set<TacoAssetType>();
                      // Hide blog entirely from the download menu unless the
                      // blog feature is enabled for this user.
                      const visibleAssetTypes = new Set(
                        DOWNLOADABLE_ASSETS.filter(
                          (m) =>
                            m.type !== TacoAssetType.BLOG_POST ||
                            isBlogPostEnabled,
                        ).map((m) => m.type),
                      );
                      return (
                        <DownloadProjectButton
                          projectId={projectId}
                          projectName={currentProject.projectName ?? ""}
                          availableAssetTypes={availableAssetTypes}
                          visibleAssetTypes={visibleAssetTypes}
                          additionalAssets={additionalAssets}
                          isPresentationEnabled={isPresentationDownloadEnabled}
                        />
                      );
                    })()}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <MoreHorizontal className="h-4 w-4" />
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleRenameClick}>
                        <Edit className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleDeleteClick}
                        className="text-red-600 dark:text-red-500 focus:text-red-600 dark:focus:text-red-500"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="default"
                    size="sm"
                    className="gap-2"
                    onClick={() => (window.location.href = "/")}
                  >
                    <Plus className="h-4 w-4" />
                    Create New
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full bg-background">
              {children || (
                <Outlet
                  context={{
                    isHelpPanelOpen,
                    setIsHelpPanelOpen,
                    expandChatPanel,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
            <DialogDescription>
              Enter a new name and description for "
              {currentProject?.projectName}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    handleRenameConfirm();
                  }
                }}
                placeholder="Enter project name"
                maxLength={nameMaxLength}
                className="border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:border-gray-400"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-right">
                {newProjectName.length}/{nameMaxLength}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                placeholder="Enter project description"
                rows={3}
                maxLength={descMaxLength}
                className="resize-none border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:border-gray-400"
              />
              <p className="text-xs text-muted-foreground text-right">
                {newProjectDescription.length}/{descMaxLength}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameConfirm}
              disabled={!newProjectName.trim()}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{currentProject?.projectName}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer className="sticky bottom-0 z-50" />
    </div>
  );
};

export default IDELayout;
